import React, { useState, useEffect, useCallback } from 'react';
import {
  Filter,
  Calendar,
  User,
  Activity,
  Eye,
  RefreshCw,
  Clock,
  MapPin,
  Download,
} from 'lucide-react';
import { auditoriaService } from '../services/api';
import { AuditLog, AuditStats } from '../types';
import toast from 'react-hot-toast';

const Auditoria: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Filtros
  const [filters, setFilters] = useState({
    action: '',
    userId: '',
    ip: '',
    startDate: '',
    endDate: '',
    limit: 50,
  });

  // Buscar logs de auditoria
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: currentPage,
      };

      const response = await auditoriaService.list(params);
      setLogs(response.logs);
      setTotalPages(response.pagination.totalPages);
      setTotalLogs(response.pagination.total);
    } catch (error) {
      toast.error('Erro ao carregar logs de auditoria');
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage]);

  // Buscar estatísticas
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await auditoriaService.getStats(30);
      setStats(response);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
      console.error('Erro:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [currentPage, fetchLogs, fetchStats]);

  useEffect(() => {
    setCurrentPage(1);
    fetchLogs();
  }, [filters, fetchLogs]);

  // Aplicar filtros
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  // Limpar filtros
  const clearFilters = () => {
    setFilters({
      action: '',
      userId: '',
      ip: '',
      startDate: '',
      endDate: '',
      limit: 50,
    });
  };

  // Exportar para CSV
  const exportToCSV = async () => {
    try {
      setExporting(true);

      // Preparar parâmetros de filtro para exportação
      const exportParams: {
        action?: string;
        userId?: string;
        ip?: string;
        startDate?: string;
        endDate?: string;
      } = {};
      if (filters.action) exportParams.action = filters.action;
      if (filters.userId) exportParams.userId = filters.userId;
      if (filters.ip) exportParams.ip = filters.ip;
      if (filters.startDate) exportParams.startDate = filters.startDate;
      if (filters.endDate) exportParams.endDate = filters.endDate;

      // Fazer download do CSV
      const blob = await auditoriaService.exportCSV(exportParams);

      // Criar URL para download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Gerar nome do arquivo com data e filtros
      const now = new Date().toISOString().split('T')[0];
      const filterSuffix = Object.keys(exportParams).length > 0 ? '_filtrado' : '';
      link.download = `auditoria_${now}${filterSuffix}.csv`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Relatório CSV exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar relatório CSV');
      console.error('Erro:', error);
    } finally {
      setExporting(false);
    }
  };

  // Formatar ação
  const formatAction = (action: string) => {
    const actionMap: { [key: string]: string } = {
      GMUD_CREATED: 'GMUD Criado',
      GMUD_UPDATED: 'GMUD Atualizado',
      GMUD_DELETED: 'GMUD Excluído',
      USER_LOGIN: 'Login de Usuário',
      USER_LOGOUT: 'Logout de Usuário',
      RESOURCE_ACCESS: 'Acesso a Recurso',
      CONFIG_CHANGED: 'Configuração Alterada',
      BACKUP_OPERATION: 'Operação de Backup',
      CRITICAL_ERROR: 'Erro Crítico',
    };

    return actionMap[action] || action;
  };

  // Formatar data
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  // Obter cor da ação
  const getActionColor = (action: string) => {
    const colorMap: { [key: string]: string } = {
      GMUD_CREATED: 'text-green-600 bg-green-100',
      GMUD_UPDATED: 'text-blue-600 bg-blue-100',
      GMUD_DELETED: 'text-red-600 bg-red-100',
      USER_LOGIN: 'text-purple-600 bg-purple-100',
      USER_LOGOUT: 'text-gray-600 bg-gray-100',
      RESOURCE_ACCESS: 'text-orange-600 bg-orange-100',
      CONFIG_CHANGED: 'text-yellow-600 bg-yellow-100',
      BACKUP_OPERATION: 'text-indigo-600 bg-indigo-100',
      CRITICAL_ERROR: 'text-red-600 bg-red-100',
    };

    return colorMap[action] || 'text-gray-600 bg-gray-100';
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900'>Auditoria do Sistema</h1>
          <p className='mt-2 text-gray-600'>
            Visualize todas as ações realizadas pelos usuários no sistema
          </p>
        </div>
        <div className='flex items-center space-x-3'>
          <button
            onClick={exportToCSV}
            disabled={exporting}
            className='btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {exporting ? (
              <RefreshCw className='h-4 w-4 mr-2 animate-spin' />
            ) : (
              <Download className='h-4 w-4 mr-2' />
            )}
            {exporting ? 'Exportando...' : 'Exportar CSV'}
          </button>
          <button
            onClick={() => {
              fetchLogs();
              fetchStats();
            }}
            className='btn-secondary flex items-center'
          >
            <RefreshCw className='h-4 w-4 mr-2' />
            Atualizar
          </button>
        </div>
      </div>

      {/* Estatísticas */}
      {!statsLoading && stats && (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
          <div className='card'>
            <div className='flex items-center'>
              <Activity className='h-8 w-8 text-blue-600 mr-3' />
              <div>
                <p className='text-sm font-medium text-gray-600'>Total de Ações</p>
                <p className='text-2xl font-bold text-gray-900'>{totalLogs}</p>
              </div>
            </div>
          </div>

          <div className='card'>
            <div className='flex items-center'>
              <User className='h-8 w-8 text-green-600 mr-3' />
              <div>
                <p className='text-sm font-medium text-gray-600'>Usuários Ativos</p>
                <p className='text-2xl font-bold text-gray-900'>{stats.usersStats.length}</p>
              </div>
            </div>
          </div>

          <div className='card'>
            <div className='flex items-center'>
              <Clock className='h-8 w-8 text-purple-600 mr-3' />
              <div>
                <p className='text-sm font-medium text-gray-600'>Período</p>
                <p className='text-2xl font-bold text-gray-900'>{stats.period}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className='card'>
        <h3 className='text-lg font-semibold text-gray-900 mb-4 flex items-center'>
          <Filter className='h-5 w-5 mr-2' />
          Filtros
        </h3>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>Ação</label>
            <select
              value={filters.action}
              onChange={e => handleFilterChange('action', e.target.value)}
              className='input'
            >
              <option value=''>Todas as ações</option>
              <option value='GMUD_CREATED'>GMUD Criado</option>
              <option value='GMUD_UPDATED'>GMUD Atualizado</option>
              <option value='GMUD_DELETED'>GMUD Excluído</option>
              <option value='USER_LOGIN'>Login de Usuário</option>
              <option value='USER_LOGOUT'>Logout de Usuário</option>
              <option value='RESOURCE_ACCESS'>Acesso a Recurso</option>
              <option value='CONFIG_CHANGED'>Configuração Alterada</option>
            </select>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>Usuário ID</label>
            <input
              type='text'
              value={filters.userId}
              onChange={e => handleFilterChange('userId', e.target.value)}
              className='input'
              placeholder='ID do usuário'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>IP Address</label>
            <input
              type='text'
              value={filters.ip}
              onChange={e => handleFilterChange('ip', e.target.value)}
              className='input'
              placeholder='Endereço IP'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>Data Inicial</label>
            <input
              type='date'
              value={filters.startDate}
              onChange={e => handleFilterChange('startDate', e.target.value)}
              className='input'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>Data Final</label>
            <input
              type='date'
              value={filters.endDate}
              onChange={e => handleFilterChange('endDate', e.target.value)}
              className='input'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Limite por página
            </label>
            <select
              value={filters.limit}
              onChange={e => handleFilterChange('limit', e.target.value)}
              className='input'
            >
              <option value={25}>25 registros</option>
              <option value={50}>50 registros</option>
              <option value={100}>100 registros</option>
            </select>
          </div>
        </div>

        <div className='mt-4 flex justify-end'>
          <button onClick={clearFilters} className='btn-secondary'>
            Limpar Filtros
          </button>
        </div>
      </div>

      {/* Lista de Logs */}
      <div className='card'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-gray-900 flex items-center'>
            <Eye className='h-5 w-5 mr-2' />
            Logs de Auditoria
          </h3>
          <div className='text-sm text-gray-600'>
            Página {currentPage} de {totalPages} ({totalLogs} registros)
          </div>
        </div>

        {loading ? (
          <div className='flex items-center justify-center h-64'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
          </div>
        ) : (
          <div className='overflow-hidden'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Ação
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Usuário
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    IP Address
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Data/Hora
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Detalhes
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {logs.map(log => (
                  <tr key={log.id} className='hover:bg-gray-50'>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(
                          log.acao
                        )}`}
                      >
                        {formatAction(log.acao)}
                      </span>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {log.usuario_id || 'N/A'}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                      <div className='flex items-center'>
                        <MapPin className='h-4 w-4 mr-1 text-gray-400' />
                        {log.ip_address || 'N/A'}
                      </div>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                      <div className='flex items-center'>
                        <Calendar className='h-4 w-4 mr-1 text-gray-400' />
                        {formatDate(log.timestamp)}
                      </div>
                    </td>
                    <td className='px-6 py-4 text-sm text-gray-900'>
                      <details className='cursor-pointer'>
                        <summary className='hover:text-primary-600'>Ver detalhes</summary>
                        <pre className='mt-2 p-2 bg-gray-50 rounded text-xs overflow-auto max-h-32'>
                          {JSON.stringify(log.detalhes, null, 2)}
                        </pre>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginação */}
        {totalPages > 1 && (
          <div className='mt-6 flex items-center justify-between'>
            <div className='text-sm text-gray-700'>
              Mostrando página {currentPage} de {totalPages}
            </div>
            <div className='flex space-x-2'>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className='btn-secondary disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Anterior
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className='btn-secondary disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Auditoria;
